import Button from 'antd/lib/button';
import Collapse from 'antd/lib/collapse';
import List from 'antd/lib/list';
import Space from 'antd/lib/space';
import Tag from 'antd/lib/tag';
import Text from 'antd/lib/typography/Text';
import React from 'react';
import { AppComponentProps, SetupStep, SetupStepType } from '../../../typescriptHelpers';
import { AutomationTip, HelpTip, setupModuleStyle } from '../shared';
import Icon, { ThunderboltOutlined, SettingOutlined, CaretRightOutlined } from '@ant-design/icons';
import { selectUserIsAdmin, selectWasAppCreatedAfterQualitativeOnboarding } from '../../../selectors/selectors';
import { useSelector } from 'react-redux';
import { RootState } from '../../../reducers/reducers';
import { getSetupStepLocalData } from '../../../helpers/localStorageHelper';
import { displayTextForCategory, iconForAutomationType } from '../../../helpers/displayHelpers';
import { antGrey, runwayColors, tagStyle } from '../../styles/globalStyles';
import { AppAutomationSettings } from '../../automations/AppAutomationSettings';
import { Automation, AutomationCategory } from '../../../types';
import { PlanRestrictedFeatureWrapper } from '../../planRestrictions/PlanRestrictedFeatureWrapper';
import { selectAutomationsByCategory } from '@/selectors/memoizedSelectors';

const SetupViewComponent: React.FC<AppComponentProps> = (props: AppComponentProps) => {
    const automationsByCategory = useSelector((state: RootState) => selectAutomationsByCategory(state, props.appId));
    const categorySortOrder = [
        AutomationCategory.ReleaseCycle,
        AutomationCategory.AppStoreTasks,
        AutomationCategory.BetaTesting,
        AutomationCategory.ReleaseHygiene,
        AutomationCategory.General,
    ];
    return (
        <div
            style={{
                ...setupModuleStyle,
                padding: 0,
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            {categorySortOrder.map((category, index) => (
                <CollapsibleAutomationsList
                    key={category}
                    category={category as AutomationCategory}
                    automations={automationsByCategory[category]}
                    {...props}
                    showOpen={index === 0}
                />
            ))}
        </div>
    );
};

interface CollapsibleAutomationsListProps extends AppComponentProps {
    category: AutomationCategory;
    automations: Automation[];
    showOpen: boolean;
}

const CollapsibleAutomationsList: React.FC<CollapsibleAutomationsListProps> = (
    props: CollapsibleAutomationsListProps,
) => {
    const [showAutomationSettings, setShowAutomationSettings] = React.useState(false);
    const [automationIdSelectedForSettings, setAutomationIdSelectedForSettings] = React.useState(null);

    return (
        <Collapse
            bordered={false}
            expandIcon={({ isActive }) => (
                <CaretRightOutlined
                    style={{
                        color: antGrey.grey6,
                        fontSize: 22,
                    }}
                    rotate={isActive ? 90 : 0}
                />
            )}
            defaultActiveKey={props.showOpen ? props.category : null}
        >
            <Collapse.Panel
                key={props.category}
                style={{ padding: 10 }}
                header={
                    <Space size={12}>
                        <Text style={{ color: antGrey.grey8, fontSize: 14, fontWeight: 600 }}>
                            {displayTextForCategory(props.category)}
                        </Text>
                    </Space>
                }
            >
                <List
                    header={null}
                    pagination={false}
                    bordered={false}
                    dataSource={props.automations}
                    renderItem={(automation) => {
                        return (
                            <List.Item key={automation.id} style={{ borderBottomWidth: 0, padding: 6 }}>
                                <PlanRestrictedFeatureWrapper automationType={automation.type}>
                                    {(isDisabled) => (
                                        <div
                                            style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                flexGrow: 1,
                                                backgroundColor: 'white',
                                                padding: 10,
                                                borderRadius: '8px',
                                            }}
                                        >
                                            <Space size={18} align="center">
                                                <Icon
                                                    component={iconForAutomationType(automation.type)}
                                                    disabled={isDisabled}
                                                    style={{
                                                        fontSize: 16,
                                                        color: automation.enabled ? antGrey.grey7 : antGrey.grey5,
                                                        paddingTop: 4,
                                                    }}
                                                />
                                                <Text
                                                    disabled={isDisabled}
                                                    type={automation.enabled ? undefined : 'secondary'}
                                                >
                                                    {automation.name}
                                                </Text>
                                                <PlanRestrictedFeatureWrapper
                                                    automationType={automation.type}
                                                    requiresTooltip
                                                />
                                            </Space>
                                            <Space>
                                                {automation.enabled ?
                                                    <Tag
                                                        color="white"
                                                        style={{
                                                            ...tagStyle,
                                                            fontSize: 10,
                                                            borderColor: runwayColors.statusGreen,
                                                            color: runwayColors.statusGreen,
                                                        }}
                                                    >
                                                        Enabled
                                                    </Tag>
                                                :   <Tag
                                                        color="white"
                                                        style={{
                                                            ...tagStyle,
                                                            fontSize: 10,
                                                            borderColor: antGrey.grey6,
                                                            color: antGrey.grey6,
                                                        }}
                                                    >
                                                        Disabled
                                                    </Tag>
                                                }
                                                <Button
                                                    ghost
                                                    icon={
                                                        <SettingOutlined
                                                            style={{
                                                                color: isDisabled ? antGrey.grey6 : runwayColors.info,
                                                            }}
                                                        />
                                                    }
                                                    onClick={() => {
                                                        setAutomationIdSelectedForSettings(automation.id);
                                                        setShowAutomationSettings(true);
                                                    }}
                                                />
                                            </Space>
                                        </div>
                                    )}
                                </PlanRestrictedFeatureWrapper>
                            </List.Item>
                        );
                    }}
                ></List>
            </Collapse.Panel>
            {automationIdSelectedForSettings && (
                <AppAutomationSettings
                    onAutomationSelected={(automationId) => setAutomationIdSelectedForSettings(automationId)}
                    appId={props.appId}
                    orgId={props.orgId}
                    onClose={() => setShowAutomationSettings(false)}
                    open={showAutomationSettings}
                    automationId={automationIdSelectedForSettings}
                    onAfterClose={() => {
                        setAutomationIdSelectedForSettings(null);
                    }}
                />
            )}
        </Collapse>
    );
};

const InfoViewComponent: React.FC<AppComponentProps> = () => {
    return (
        <Space direction="vertical">
            <AutomationTip>
                <span style={{ color: antGrey.grey8, fontSize: 14, fontWeight: 400 }}>
                    When you have automations turned on, you’ll see the lightning symbol showing up in various locations
                    in the Runway app.
                    <br />
                    <br />
                    This might be a reminder that Runway is ready to take actions on your behalf (as in the Release
                    timeline, or in the Schedule drawer), or it’s pointing you to a way to check on the status of your
                    pending or completed automations (as in the <b>Automations</b> tab found on many steps).
                </span>
            </AutomationTip>
            <AutomationTip>
                Runway can automatically pause or accelerate phased releases based on product health thresholds that you
                define — using App Store ratings or even crash rates or product analytics events (when you connect
                stability monitoring and observability + analytics tools)!
            </AutomationTip>
            <HelpTip>
                Questions, or want a walkthrough?{' '}
                <Button type="link" style={{ padding: 0, display: 'inline', marginLeft: 5 }} href={`#hs-chat-open`}>
                    Let us know!
                </Button>
            </HelpTip>
        </Space>
    );
};

export const AutomationsStep: SetupStep = {
    title: 'Choose your automations.',
    description: () => {
        return 'During each release, Runway can perform a wide variety of common tasks to help streamline chores around scheduling, beta testing, app stores, tagging, and more.';
    },
    icon: () => ThunderboltOutlined,
    SetupViewComponent,
    InfoViewComponent,
    listTitle: 'Automations',
    listDescription: 'Let Runway handle common tasks during each release.',
    integrationsRecommendationBannerData: null,
    isCompleteSelector: (root, { appId }) => {
        const isUserAdmin = selectUserIsAdmin(root);
        const wasAppCreatedAfterQualitativeOnboarding = selectWasAppCreatedAfterQualitativeOnboarding(root, appId);

        const wasVisited = getSetupStepLocalData(appId, SetupStepType.Automations)?.hasBeenVisited;
        return wasAppCreatedAfterQualitativeOnboarding && isUserAdmin ? wasVisited : true;
    },
};
